import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CustomModalButtons } from "../../../constants/global.constants";

import CustomModal from "../../../packages/CustomModal";
import IconButton from "../../../packages/IconButton";
import { BackIcon } from "../../../packages/SVGs";

import {
  ChemicalLibDropdown,
  ChemicalLibInputBox,
} from "./ChemicalLibFormControls";
import { formatChemicalDataToFormData } from "./ChemicalLibraryAddEditDialog.util";

import "./ChemicalLibraryAddEditDialog.scss";

const ChemicalLibHeader = ({ onClose }) => {
  const { t } = useTranslation();
  return (
    <div>
      <IconButton tooltip={t("ValueStrings.back")} onClick={onClose}>
        <BackIcon />
      </IconButton>
      {t("chemicalLibrary.editChemicalDlgHeading")}
    </div>
  );
};

const ChemicalLibraryAddEditDialog = ({
  onClose,
  chemicalRowData,
  isLoading,
  onSubmit,
  shouldResetData,
}) => {
  const { t } = useTranslation();

  const chemicalFormData = useMemo(() => {
    return chemicalRowData
      ? formatChemicalDataToFormData(t, chemicalRowData)
      : null;
  }, []);

  const [invalidFields, setInvalidFields] = useState([]);
  const [formControls, setFormControls] = useState(chemicalFormData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const handleModalClose = async ({ target: { id } }) => {
    if (id === CustomModalButtons.CONFIRM) {
      const updatedChemicalData = {
        ...chemicalRowData,
        price: formControls.price.value,
        unit: formControls.unit.value,
      };
      onSubmit(updatedChemicalData);
    }
  };

  const handleControlChange = (event) => {
    const { id, data, hasError } = event;
    let newControls = { ...formControls };

    newControls = {
      ...newControls,
      [id]: { ...newControls[id], value: data.value },
    };

    if (hasError !== undefined) {
      setInvalidFields((prev) =>
        hasError ? [...prev, id] : prev.filter((fieldId) => fieldId !== id),
      );
    }

    const hasChanged = !isNaN(data.value)
      ? parseFloat(data.value) !== parseFloat(formControls[id].value)
      : data.value !== formControls[id].value;

    if (hasChanged) {
      setHasUnsavedChanges(true);
      setFormControls(newControls);
    }
  };

  const isFormInvalid = useMemo(
    () => invalidFields.length > 0,
    [invalidFields],
  );

  const isConfirmDisabled = useMemo(
    () => isFormInvalid || !hasUnsavedChanges,
    [isFormInvalid, hasUnsavedChanges],
  );

  useEffect(() => {
    if (shouldResetData) {
      setFormControls(chemicalFormData);
      setHasUnsavedChanges(false);
    }
  }, [shouldResetData, chemicalFormData]);

  return (
    <>
      <CustomModal
        header={<ChemicalLibHeader onClose={onClose} />}
        confirmBtn={t("chemicalLibrary.saveChemical")}
        onModalClose={handleModalClose}
        width="850px"
        confirmBtnDisabled={isConfirmDisabled}
        hideCloseIcon
        isLoading={isLoading}
      >
        <div className="add-edit-chemical-form--container">
          {Object.values(formControls).map((control) => (
            <div className="column bottom-margin-0" key={control.id}>
              {control.type === "dropdown" ? (
                <ChemicalLibDropdown
                  control={control}
                  onControlChange={handleControlChange}
                />
              ) : (
                <ChemicalLibInputBox
                  control={control}
                  onControlChange={handleControlChange}
                />
              )}
            </div>
          ))}
        </div>
      </CustomModal>
    </>
  );
};

export default ChemicalLibraryAddEditDialog;
